import { DaysOfWeek } from "@/types/gql/graphql";
import * as z from "zod";

import { CalendarSelector, DaysOfMonth, TimeInput } from "components/form-control";
import { DaysOfWeekInput, arrDay } from "./time-span.const";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { MonthsOfYear } from "components/form-control/month-of-year";
import { TimeSpanComponentProps } from "./time-span.type";
import { TimeSpanInput } from "types";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

const timeSpanSchema = z.object({
  startDate: z.union([z.date(), z.string(), z.null()]).optional(),
  endDate: z.union([z.date(), z.string(), z.null()]).optional(),
  timeOfDayStart: z.string().optional(),
  timeOfDayEnd: z.string().optional(),
  daysOfWeek: z.array(z.enum(DaysOfWeek)).optional(),
  daysOfMonth: z.array(z.number()).optional(),
  months: z.array(z.number()).optional(),
  designatedPeriod: z.string().optional(),
  designatedPeriodExcept: z.boolean().optional(),
});

type TimeSpanFormValues = z.infer<typeof timeSpanSchema>;

export interface DaysOfWeekModalProps extends Pick<TimeSpanComponentProps, "options" | "label"> {
  timeSpanData: TimeSpanInput;
  onDismiss: () => void;
  onApply: (input: TimeSpanInput) => void;
}

export const TimeSpanModal = ({ timeSpanData, onDismiss, onApply, label, options }: DaysOfWeekModalProps) => {
  const form = useForm<TimeSpanFormValues>({
    resolver: zodResolver(timeSpanSchema),
    defaultValues: {
      startDate: timeSpanData.startDate ?? null,
      endDate: timeSpanData.endDate ?? null,
      timeOfDayStart: timeSpanData?.timeOfDayStart ?? "",
      timeOfDayEnd: timeSpanData?.timeOfDayEnd ?? "",
      daysOfWeek: timeSpanData?.daysOfWeek ?? [],
      daysOfMonth: timeSpanData?.daysOfMonth ?? [],
      months: timeSpanData?.months ?? [],
      designatedPeriod: timeSpanData?.designatedPeriod ?? "",
      designatedPeriodExcept: timeSpanData?.designatedPeriodExcept ?? false,
    },
  });

  const { handleSubmit, control, watch } = form;
  const [daysOfWeek, setDaysOfWeek] = useState(() => syncDaysOfWeek(timeSpanData.daysOfWeek ?? []));

  const onChangeDaysOfWeek = (index: number) => {
    const newDaysOfWeek = daysOfWeek;
    newDaysOfWeek[index].isCheck = !newDaysOfWeek[index].isCheck;
    setDaysOfWeek([...newDaysOfWeek]);
  };

  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const enableApply = useMemo((): boolean => {
    //If the startDate and endDate, required the startDate, endDate
    //Only for Restriction modal
    if (options?.showSelectDate) {
      return !!startDate && !!endDate;
    }
    return true;
  }, [startDate, endDate, options?.showSelectDate]);

  const handleSave = (values: TimeSpanFormValues) => {
    const newDaysOfWeek = getSelectedDaysOfWeek(daysOfWeek);
    const newTimeSpanData = {
      ...timeSpanData,
      ...values,
      daysOfWeek: newDaysOfWeek,
    };
    onApply(newTimeSpanData);
  };

  return (
    <Dialog open>
      <DialogContent className="!max-w-2xl">
        <DialogHeader>
          <DialogTitle>{`Edit a ${label}`}</DialogTitle>
          <DialogDescription>Time span information's</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-6">
            {(options?.showSelectDate || options?.showTimeOfDay) && (
              <div className="flex gap-10 items-end">
                <FormField
                  control={control}
                  name="startDate"
                  render={({ field: { value, onChange } }) => (
                    <FormItem>
                      <FormLabel>{options?.showSelectDate ? "Start date" : "Time of day"}</FormLabel>
                      <FormControl>
                        <div className="flex flex-row items-end gap-2">
                          {options?.showSelectDate && (
                            <CalendarSelector
                              selectedDate={value ?? undefined}
                              onChangeValue={onChange}
                              maxDate={endDate ?? undefined}
                            />
                          )}
                          {options?.showTimeOfDay && (
                            <TimeInput
                              times={watch("timeOfDayStart") ?? ""}
                              onChangeValue={(value) => form.setValue("timeOfDayStart", value)}
                            />
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="endDate"
                  render={({ field: { value, onChange } }) => (
                    <FormItem>
                      <FormLabel>{options?.showSelectDate ? "End date" : ""}</FormLabel>
                      <FormControl>
                        <div className="flex flex-row justify-end items-end gap-2">
                          {options?.showSelectDate && (
                            <CalendarSelector
                              selectedDate={value ?? undefined}
                              onChangeValue={onChange}
                              minDate={watch("startDate") ?? undefined}
                            />
                          )}
                          {options?.showTimeOfDay && (
                            <TimeInput
                              times={watch("timeOfDayEnd") ?? ""}
                              onChangeValue={(value) => form.setValue("timeOfDayEnd", value)}
                            />
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <FormField
              control={control}
              name="daysOfWeek"
              render={() => (
                <FormItem>
                  <FormLabel>Days</FormLabel>
                  <FormControl>
                    <div className="flex flex-wrap">
                      {daysOfWeek.map((day, index) => (
                        <div className="flex items-center space-x-2 mr-2" key={`day-of-week-${day.name}-${index}`}>
                          <Checkbox
                            onClick={() => onChangeDaysOfWeek(index)}
                            checked={day.isCheck}
                            id={`day-${day.name}`}
                          />
                          <label
                            htmlFor={`day-${day.name}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            {day.display}
                          </label>
                        </div>
                      ))}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="daysOfMonth"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>Days of month</FormLabel>
                  <FormControl>
                    <div>
                      <DaysOfMonth days={value ?? []} onChange={onChange} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="months"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>Months</FormLabel>
                  <FormControl>
                    <div>
                      <MonthsOfYear months={value ?? []} onChange={onChange} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {options?.showDesignatedPeriod && (
              <FormField
                control={control}
                name="designatedPeriod"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Designated Period</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {options?.showDesignatedPeriodExcept && (
              <FormField
                control={control}
                name="designatedPeriodExcept"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex items-center space-x-2">
                        <Checkbox checked={value} onCheckedChange={onChange} id="designated-period-except" />
                        <label
                          htmlFor="designated-period-except"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Designated Period Except
                        </label>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter className="sm:justify-between">
              <Button type="button" onClick={onDismiss} variant="secondary">
                Cancel
              </Button>
              <Button disabled={!enableApply} type="button" onClick={handleSubmit(handleSave)}>
                Apply
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export const syncDaysOfWeek = (list: DaysOfWeek[]): DaysOfWeekInput[] => {
  const arrayDay = [];
  for (const item of arrDay) {
    arrayDay.push({ ...item, isCheck: list.includes(item.name) });
  }
  return arrayDay;
};

export const getSelectedDaysOfWeek = (list: DaysOfWeekInput[]): DaysOfWeek[] => {
  return list.reduce((previousValue: DaysOfWeek[], item) => {
    if (item.isCheck) {
      return [...previousValue, item.name];
    }
    return previousValue;
  }, []);
};
